// EmployeeEditModal.jsx
import React, { useState, useEffect } from 'react'
import { apiGet, apiPut } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import useToastContext from 'hooks/useToastContext'
import dayjs from 'dayjs'
import { useMFA } from 'context/MFAContext'
import { callWithStepUp } from 'utils/payrollMfa'

const EmployeeEditModal = ({ employee, onClose, onSuccess }) => {
  const notification = useToastContext()
  const { requireMfa } = useMFA()
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)
  const [evereeData, setEvereeData] = useState(null)
  const [locations, setLocations] = useState([])
  const [field, setField] = useState('position')
  const [effectiveDate, setEffectiveDate] = useState(dayjs().format('YYYY-MM-DD'))
  
  // ─── Field-specific form data ───
  const [formData, setFormData] = useState({
    // Position fields
    payType: '',
    payRate: '',
    expectedWeeklyHours: '',
    // Bank Account fields
    bankName: '',
    accountName: '',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
    // Address fields
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    // Taxpayer ID
    taxpayerIdentifier: '',
    // Work Location
    workLocationId: '',
  })

  // ─── Fetch Everee data and Locations on mount ───
  useEffect(() => {
    const fetchData = async () => {
      if (!employee?._id) return
      
      setFetchingData(true)
      try {
        // Fetch Everee data
        const evereeRes = await apiGet(`${apiPath.employeeSync}/${employee._id}/sync`)
        if (evereeRes?.data?.success) {
          const evereeDetail = evereeRes.data.results.evereeDetail
          setEvereeData(evereeDetail)
          
          // Pre-fill form with Everee data
          if (evereeDetail) {
            // Position data
            setFormData(prev => ({
              ...prev,
              payType: evereeDetail.position?.payType?.toLowerCase() || employee?.wageDetails?.payType || 'salary',
              payRate: evereeDetail.position?.payRate?.amount || 
                (employee?.wageDetails?.payType === 'salary' 
                  ? employee?.wageDetails?.annualSalary 
                  : employee?.wageDetails?.hourlyRate) || 0,
              expectedWeeklyHours: evereeDetail.typicalWeeklyHours || employee?.typicalWeeklyHours || 40,
              
              // Bank Account data
              bankName: evereeDetail.bankAccounts?.[0]?.bankName || employee?.bankDetails?.bankName || '',
              accountName: evereeDetail.bankAccounts?.[0]?.accountName || employee?.bankDetails?.accountName || '',
              accountType: evereeDetail.bankAccounts?.[0]?.accountType?.toLowerCase() || employee?.bankDetails?.accountType || 'checking',
              routingNumber: evereeDetail.bankAccounts?.[0]?.routingNumber || employee?.bankDetails?.routingNumber || '',
              accountNumber: evereeDetail.bankAccounts?.[0]?.accountNumberLast4 || employee?.bankDetails?.accountNumber || '',
              
              // Address data
              addressLine1: evereeDetail.homeAddress?.line1 || employee?.addressLine1 || '',
              addressLine2: evereeDetail.homeAddress?.line2 || employee?.addressLine2 || '',
              city: evereeDetail.homeAddress?.city || employee?.city || '',
              state: evereeDetail.homeAddress?.state || employee?.stateID || '',
              postalCode: evereeDetail.homeAddress?.postalCode || employee?.postalCode || '',
              
              // Taxpayer ID
              taxpayerIdentifier: evereeDetail.taxpayerIdentifier || employee?.taxpayerIdentifier || '',
              
              // Work Location
              workLocationId: evereeDetail.legalWorkAddress?.workLocationId || employee?.everee?.workLocationId || '',
            }))
          }
        }

        // Fetch locations for dropdown
        const locationsRes = await apiGet(apiPath.getLocations)
        if (locationsRes?.data?.success) {
          setLocations(locationsRes.data.results || [])
        }

      } catch (err) {
        console.log('Error fetching data:', err.message)
        // Fallback to employee data
        setFormData(prev => ({
          ...prev,
          payType: employee?.wageDetails?.payType || 'salary',
          payRate: employee?.wageDetails?.payType === 'salary' 
            ? employee?.wageDetails?.annualSalary || 0 
            : employee?.wageDetails?.hourlyRate || 0,
          expectedWeeklyHours: employee?.typicalWeeklyHours || 40,
          bankName: employee?.bankDetails?.bankName || '',
          accountName: employee?.bankDetails?.accountName || '',
          accountType: employee?.bankDetails?.accountType || 'checking',
          routingNumber: employee?.bankDetails?.routingNumber || '',
          accountNumber: employee?.bankDetails?.accountNumber || '',
          addressLine1: employee?.addressLine1 || '',
          addressLine2: employee?.addressLine2 || '',
          city: employee?.city || '',
          state: employee?.stateID || '',
          postalCode: employee?.postalCode || '',
          taxpayerIdentifier: employee?.taxpayerIdentifier || '',
          workLocationId: employee?.everee?.workLocationId || '',
        }))
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [employee])

  const handleFieldChange = (newField) => {
    setField(newField)
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }))
  }

  // const handleSubmit = async (e) => {
  //   e.preventDefault()
  //   if (!employee?._id) return

  //   setLoading(true)
  //   try {
  //     let payload = {
  //       field: field,
  //       effectiveDate: effectiveDate
  //     }

  //     // Build field-specific payload with all values
  //     switch (field) {
  //       case 'position':
  //         payload = {
  //           ...payload,
  //           payType: formData.payType,
  //           payRate: formData.payRate,
  //           expectedWeeklyHours: formData.expectedWeeklyHours
  //         }
  //         break
        
  //       case 'bankAccount':
  //         payload = {
  //           ...payload,
  //           bankName: formData.bankName,
  //           accountName: formData.accountName,
  //           accountType: formData.accountType,
  //           routingNumber: formData.routingNumber,
  //           accountNumber: formData.accountNumber
  //         }
  //         break
        
  //       case 'legalLocation':
  //         payload = {
  //           ...payload,
  //           workLocationId: parseInt(formData.workLocationId)
  //         }
  //         break
        
  //       case 'address':
  //         payload = {
  //           ...payload,
  //           addressLine1: formData.addressLine1,
  //           addressLine2: formData.addressLine2,
  //           city: formData.city,
  //           state: formData.state,
  //           postalCode: formData.postalCode
  //         }
  //         break
        
  //       case 'taxpayerId':
  //         payload = {
  //           ...payload,
  //           taxpayerIdentifier: formData.taxpayerIdentifier
  //         }
  //         break
        
  //       default:
  //         break
  //     }

  //     const res = await apiPut(`${apiPath.employeeSync}/${employee._id}/sync`, payload)
      
  //     if (res?.data?.success) {
  //       notification.success(res.data.results?.message || 'Employee updated in Everee successfully')
  //       if (onSuccess) onSuccess()
  //       onClose()
  //     } else {
  //       notification.error(res?.data?.message || 'Update failed')
  //     }
  //   } catch (err) {
  //     notification.error('Something went wrong')
  //     console.error('Update error:', err)
  //   } finally {
  //     setLoading(false)
  //   }
  // }

  // Helper to get friendly status text
    const handleSubmit = async (e) => {
    e.preventDefault()
    if (!employee?._id) return

    setLoading(true)
    try {
      let payload = {
        field: field,
        effectiveDate: effectiveDate
      }

      // Build field-specific payload with all values
      switch (field) {
        case 'position':
          payload = {
            ...payload,
            payType: formData.payType,
            payRate: formData.payRate,
            expectedWeeklyHours: formData.expectedWeeklyHours
          }
          break

        case 'bankAccount':
          payload = {
            ...payload,
            bankName: formData.bankName,
            accountName: formData.accountName,
            accountType: formData.accountType,
            routingNumber: formData.routingNumber,
            accountNumber: formData.accountNumber
          }
          break

        case 'legalLocation':
          payload = {
            ...payload,
            workLocationId: parseInt(formData.workLocationId)
          }
          break

        case 'address':
          payload = {
            ...payload,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            postalCode: formData.postalCode
          }
          break

        case 'taxpayerId':
          payload = {
            ...payload,
            taxpayerIdentifier: formData.taxpayerIdentifier
          }
          break

        default:
          break
      }

      const res = await callWithStepUp(requireMfa, (extra) =>
        apiPut(`${apiPath.employeeSync}/${employee._id}/sync`, { ...payload, ...extra })
      )
      if (!res) {
        notification.error('Verification cancelled')
        return
      }

      if (res?.data?.success) {
        notification.success(res.data.results?.message || 'Employee updated in Everee successfully')
        if (onSuccess) onSuccess()
        onClose()
      } else {
        notification.error(res?.data?.message || 'Update failed')
      }
    } catch (err) {
      notification.error('Something went wrong')
      console.error('Update error:', err)
    } finally {
      setLoading(false)
    }
  }
  
  const getFriendlyStatus = (status) => {
    const statusMap = {
      'NEEDS_VERIFICATION': 'Pending Verification',
      'SENT_FOR_VERIFICATION': 'Verification In Progress',
      'VERIFIED': 'Verified',
      'VERIFICATION_FAILED': 'Verification Failed',
      'UNABLE_TO_VERIFY': 'Unable to Verify',
      'ONBOARDING': 'Onboarding',
      'ACTIVE': 'Active',
      'SEPARATED': 'Separated'
    }
    return statusMap[status] || status
  }

  const renderFieldForm = () => {
    if (fetchingData) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin text-2xl mb-2">⟳</div>
          <p className="text-sm text-gray-500">Loading current Everee data...</p>
        </div>
      )
    }

    switch (field) {
      case 'position':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Type</label>
              <select
                name="payType"
                value={formData.payType}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option key='hourly' value="hourly">Hourly</option>
                <option key='hourly' value="salary">Salary</option>
                <option key='contractor' value="contractor">Contractor</option>
              </select>
              {evereeData?.position?.payType && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.position.payType}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.payType === 'salary' ? 'Annual Salary' : 'Hourly Rate'} ($)
              </label>
              <input
                type="number"
                name="payRate"
                value={formData.payRate}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                step="0.01"
                min="0"
                required
              />
              {evereeData?.position?.payRate && (
                <p className="text-xs text-gray-500 mt-1">
                  Current in Everee: ${parseFloat(evereeData.position.payRate.amount).toFixed(2)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expected Weekly Hours</label>
              <input
                type="number"
                name="expectedWeeklyHours"
                value={formData.expectedWeeklyHours}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                min="1"
                max="168"
                required
              />
              {evereeData?.typicalWeeklyHours && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.typicalWeeklyHours} hours</p>
              )}
            </div>
          </div>
        )

      case 'bankAccount':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input
                type="text"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.bankAccounts?.[0]?.bankName && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.bankAccounts[0].bankName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
              <input
                type="text"
                name="accountName"
                value={formData.accountName}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.bankAccounts?.[0]?.accountName && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.bankAccounts[0].accountName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
              <select
                name="accountType"
                value={formData.accountType}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
              >
                <option key='checking' value="checking">Checking</option>
                <option key='saving' value="savings">Savings</option>
              </select>
              {evereeData?.bankAccounts?.[0]?.accountType && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.bankAccounts[0].accountType}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
              <input
                type="text"
                name="routingNumber"
                value={formData.routingNumber}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                maxLength="9"
                required
              />
              {evereeData?.bankAccounts?.[0]?.routingNumber && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.bankAccounts[0].routingNumber}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.bankAccounts?.[0]?.accountNumberLast4 && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: ••••{evereeData.bankAccounts[0].accountNumberLast4}</p>
              )}
            </div>
            <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
              ⚠️ Updating bank account affects all not-yet-approved payments. Please confirm with HR before proceeding.
            </div>
          </div>
        )

      case 'legalLocation':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Work Location</label>
              <select
                name="workLocationId"
                value={formData.workLocationId}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              >
                <option value="">Select a location</option>
                {locations.map(loc => (
                  <option key={loc._id} value={loc.evereeId || loc._id}>
                    {loc.name}
                  </option>
                ))}
              </select>
              {evereeData?.legalWorkAddress?.workLocationId && (
                <p className="text-xs text-gray-500 mt-1">
                  Current in Everee: {evereeData.legalWorkAddress.name || `Location ID: ${evereeData.legalWorkAddress.workLocationId}`}
                  {evereeData.legalWorkAddress.address && (
                    <span className="block text-gray-400">
                      {evereeData.legalWorkAddress.address.city}, {evereeData.legalWorkAddress.address.state}
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
              ⚠️ Changing work location affects tax jurisdiction.
            </div>
          </div>
        )

      case 'address':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
              <input
                type="text"
                name="addressLine1"
                value={formData.addressLine1}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.homeAddress?.line1 && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.homeAddress.line1}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                name="addressLine2"
                value={formData.addressLine2}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
              {evereeData?.homeAddress?.line2 && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.homeAddress.line2}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.homeAddress?.city && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.homeAddress.city}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="e.g., CA"
                required
              />
              {evereeData?.homeAddress?.state && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.homeAddress.state}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                required
              />
              {evereeData?.homeAddress?.postalCode && (
                <p className="text-xs text-gray-500 mt-1">Current in Everee: {evereeData.homeAddress.postalCode}</p>
              )}
            </div>
          </div>
        )

      case 'taxpayerId':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Taxpayer Identifier (SSN/EIN)</label>
              <input
                type="text"
                name="taxpayerIdentifier"
                value={formData.taxpayerIdentifier}
                onChange={handleInputChange}
                className="w-full border rounded-md px-3 py-2 text-sm"
                placeholder="123-45-6789"
                required
              />
              {evereeData?.taxpayerIdentifier && (
                <p className="text-xs text-gray-500 mt-1">
                  Current in Everee: •••-••-{evereeData.taxpayerIdentifier.slice(-4)}
                </p>
              )}
              {evereeData?.tinVerificationStatus && (
                <p className="text-xs text-gray-500 mt-1">
                  Verification Status: <span className={`font-medium ${
                    evereeData.tinVerificationStatus === 'VERIFIED' ? 'text-green-600' : 
                    evereeData.tinVerificationStatus === 'VERIFICATION_FAILED' ? 'text-red-600' : 
                    'text-yellow-600'
                  }`}>{getFriendlyStatus(evereeData.tinVerificationStatus)}</span>
                </p>
              )}
            </div>
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              ⚠️ Updating Taxpayer ID requires verification. Please ensure the new ID is valid.
            </div>
          </div>
        )

      default:
        return <p className="text-sm text-gray-500">Select a field to update</p>
    }
  }

  const getFieldLabel = () => {
    const labels = {
      'position': 'Position & Pay',
      'bankAccount': 'Bank Account',
      'legalLocation': 'Work Location',
      'address': 'Home Address',
      'taxpayerId': 'Taxpayer ID'
    }
    return labels[field] || 'Update Field'
  }

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
      <div className='bg-white rounded-lg p-6 w-[550px] max-h-[90vh] overflow-y-auto'>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className='flex justify-between items-center mb-4 border-b pb-3'>
            <div>
              <h4 className='font-semibold text-lg'>Update {employee?.name}</h4>
              <p className='text-xs text-gray-500'>Employee ID: {employee?.employeeId}</p>
            </div>
            <button 
              type='button' 
              onClick={onClose} 
              className='text-gray-400 hover:text-gray-600 text-2xl leading-none'
            >
              ×
            </button>
          </div>

          <div className='space-y-4'>
            {/* Field Selection */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Field to Update</label>
              <select
                value={field}
                onChange={(e) => handleFieldChange(e.target.value)}
                className='w-full border rounded-md px-3 py-2 text-sm'
              >
                <option key='position' value='position'>Position (Pay Rate/Type)</option>
                <option key='bankAccount' value='bankAccount'>Bank Account</option>
                <option key='legalLocation' value='legalLocation'>Work Location</option>
                <option key='address' value='address'>Home Address</option>
                <option key='taxpayerId' value='taxpayerId'>Taxpayer ID</option>
              </select>
            </div>

            {/* Effective Date */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>Effective Date</label>
              <input
                type='date'
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className='w-full border rounded-md px-3 py-2 text-sm'
                required
              />
            </div>

            {/* Divider */}
            <div className='border-t pt-4'>
              <h5 className='text-sm font-semibold text-gray-700 mb-3'>{getFieldLabel()}</h5>
              {renderFieldForm()}
            </div>
          </div>

          {/* Footer */}
          <div className='flex justify-end gap-2 mt-6 pt-4 border-t'>
            <button 
              type='button' 
              onClick={onClose} 
              className='px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300'
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type='submit' 
              className='px-4 py-2 text-sm bg-[#4F46E5] text-white rounded-md hover:bg-[#4338CA] disabled:opacity-50 flex items-center gap-2'
              disabled={loading || fetchingData}
            >
              {loading ? (
                <>
                  <span className='animate-spin'>⟳</span> Updating...
                </>
              ) : fetchingData ? (
                'Loading...'
              ) : (
                'Update in Everee'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EmployeeEditModal